import React, { useEffect, useState } from "react";
import { listSales, refundSale, resumeSale, getSale } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { useNavigate } from "react-router-dom";
import TicketPreview from "@/components/TicketPreview";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { PauseCircle, Receipt, Undo2, Eye } from "lucide-react";

const TABS = [
  { key: "aparcado", label: "Aparcados", icon: PauseCircle },
  { key: "cobrado",  label: "Cobrados",  icon: Receipt },
  { key: "abonado",  label: "Abonos",    icon: Undo2 },
];

export default function Tickets() {
  const { user, session } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("aparcado");
  const [list, setList] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = () => {
    listSales({ status: tab, limit: 100 }).then(setList);
  };
  useEffect(load, [tab]);

  const onResume = async (s) => {
    await resumeSale(s.id);
    toast.success("Ticket recuperado");
    navigate("/pos");
  };

  const onRefund = async (s) => {
    if (!session) {
      toast.error("Abre una sesión de caja primero");
      return;
    }
    if (!window.confirm(`¿Devolver ticket ${s.serie}/${s.numero}? Importe ${s.total.toFixed(2)}€`)) return;
    try {
      const abono = await refundSale(s.id, {}, user, session.id);
      toast.success(`Abono ${abono.serie}/${abono.numero} emitido`);
      setPreview(abono);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la devolución");
    }
  };

  const onView = async (s) => {
    const full = await getSale(s.id);
    setPreview(full);
  };

  return (
    <div className="h-full overflow-hidden flex flex-col bg-slate-50">
      <div className="p-5 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Operativa de tickets</div>
            <h1 className="font-display text-3xl font-semibold text-slate-900">Tickets</h1>
          </div>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`h-10 px-4 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                    tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:bg-white/50"
                  }`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll p-5">
        <div data-testid={TID.ticketsList} className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-xl overflow-hidden">
          {list.length === 0 ? (
            <div className="p-12 text-center text-slate-400">Sin tickets en esta sección.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3">Nº</th>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Cajero</th>
                  <th className="text-right px-4 py-3">Líneas</th>
                  <th className="text-right px-4 py-3">Importe</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} data-testid={TID.ticketRow(s.id)} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-sm">{s.serie}/{s.numero || "·"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{new Date(s.created_at).toLocaleString("es-ES")}</td>
                    <td className="px-4 py-3 text-sm">{s.customer_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm">{s.user_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{s.lines.length}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{s.total.toFixed(2)}€</td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      {tab === "aparcado" && (
                        <button
                          data-testid={TID.ticketResume(s.id)}
                          onClick={() => onResume(s)}
                          className="h-9 px-3 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 text-xs font-medium"
                        >
                          Recuperar
                        </button>
                      )}
                      {tab === "cobrado" && (
                        <button
                          data-testid={TID.ticketRefund(s.id)}
                          onClick={() => onRefund(s)}
                          className="h-9 px-3 rounded-md bg-red-50 text-red-700 hover:bg-red-100 text-xs font-medium"
                        >
                          <Undo2 size={12} className="inline mr-1" /> Abono
                        </button>
                      )}
                      <button
                        data-testid={TID.ticketView(s.id)}
                        onClick={() => onView(s)}
                        className="h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-medium"
                      >
                        <Eye size={12} className="inline mr-1" /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {preview && (
        <TicketPreview
          sale={preview}
          onClose={() => setPreview(null)}
          onNew={() => {
            setPreview(null);
            navigate("/pos");
          }}
        />
      )}
    </div>
  );
}
